// @flow
import React, { Component } from 'react';
import ErrorBoundary from 'app/packages/error-boundary';
import type { GetComponent } from 'app/common/lib/ReactRouter/types';
import { Box, Spinner } from 'gestalt';

// The first render should behave the same as the server for hydration purposes
let isInitialRender = true;

type Props = {
  getComponent: () => Promise<Component<*, *>>,
  path: string,
};

type State = {
  component: ?Component<*, *>,
};

class ComponentLoader extends Component<Props, State> {
  state: State = { component: null };

  componentDidMount() {
    // WARNING(PERF-823): This setState will result in this.state.component's
    // componentDidMount being called. Since ComponentLoader is called on every
    // route match, this means, any history.push() or history.replace() calls
    // will result in entire page remounting.
    this.props.getComponent().then((component) => this.setState({ component }));
  }

  render() {
    const Comp = this.state.component;
    return typeof Comp === 'function' ? (
      <ErrorBoundary name={`RouteComponentLoader:${this.props.path}`}>
        <Comp {...this.props} />
      </ErrorBoundary>
    ) : (
      <Box deprecatedPadding={5}>
        <Spinner accessibilityLabel="Loading...." show />
      </Box>
    );
  }
}

export default function getComponent(
  getComponent: () => Promise<Component<*, *> | ((props: *) => *) | (() => *)>,
  path: string,
): GetComponent {
  // eslint-disable-next-line consistent-return
  return (nextState: *, callback: *) => {
    if (__SERVER__) {
      return getComponent();
    }
    if (isInitialRender) {
      const promise = getComponent();
      promise.then(() => {
        isInitialRender = false;
      });
      return promise;
    }
    callback(null, (props: *) => (
      <ComponentLoader getComponent={getComponent} path={path} {...props} />
    ));
  };
}
